import 'Core-utils/helpers/rulesAgGridModules';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import InviteSubHeaderContent from './InvitesSubHeaderContent';
import {
    ACCESS,
    ACCESS_OPTIONS,
    ADDED_DATE,
    EMAIL_ID,
    LAST_LOGGED_IN,
    NAME,
    STATUS,
    USER_RBAC_OPTIONS,
    YES_DELETE_IT,
} from 'Core-utils/constants/constants';
import { deleteAgent, getAgentsList, inviteAgent, UpdateAgent } from 'Apis/library';
import { AgentAuthRoles, AgentDto, AgentStatus, AuthRole, KIND } from '@ampsec/platform-client';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { MenuItem } from 'Core-utils/types';
import { ConfirmationModal } from 'Components/molecules';
import { SearchBar } from 'Components/molecules';
import { RiDeleteBin6Line } from '@react-icons/all-files/ri/RiDeleteBin6Line';
import { Avatar, Chip, CustomTooltip } from 'Components/atoms';
import { formatEmailsForDisplay } from 'Core-utils/email';
import RoleDropdown from './RoleDropdown';
import { RiArrowDownSLine } from '@react-icons/all-files/ri/RiArrowDownSLine';
import _ from 'lodash';
import { AgGridBase, useClientExternalTextSearch } from 'Components/organisms/AgGridBase';
import type { CellStyle, ColDef, GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-enterprise';
import { Typography } from 'Components/atoms';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserGridContext {
    fetchAgents: AgentDto[];
    setRefreshData: React.Dispatch<React.SetStateAction<boolean>>;
    handleDeleteUser: (id: string) => void;
}

// ─── Truncating text renderer (same pattern as AgGridEngagementFlow) ──────────

const TruncatedTextCellRenderer = ({ value }: ICellRendererParams) => {
    const text = value?.toString() ?? '-';
    return (
        <div className="text-truncate bodyFont d-block" title={text}>
            {text}
        </div>
    );
};

// ─── Cell renderers ──────────────────────────────────────────────────────────

const NameCellRenderer = ({ data }: ICellRendererParams<AgentDto>) =>
    data?.firstName ? (
        <Avatar
            variant="name_avatar"
            name={`${data.firstName} ${data.lastName ?? ''}`}
            image={data.pictureUrl ?? undefined}
        />
    ) : (
        <span className="text-text-low-emphasis">—</span>
    );

const EmailCellRenderer = ({ data }: ICellRendererParams<AgentDto>) => (
    <CustomTooltip
        id={`user-grid-email-${data?.id}`}
        label={formatEmailsForDisplay(data?.email ?? '')}
        labelColor="primary"
        labelVariant="subtitle3"
        overlayPlacement="left"
        overlayContainerClassName="d-flex flex-column justify-content-center overflow-hidden w-100"
        tooltipClassName="custom-tooltip--email color-white text-white"
    >
        <div className="text-truncate bodyFont d-block" title={data?.email ?? ''}>
            {data?.email}
        </div>
    </CustomTooltip>
);

const AccessCellRenderer = ({ data, context }: ICellRendererParams<AgentDto>) => {
    const { fetchAgents, setRefreshData } = context as UserGridContext;
    const [value, setValue] = useState<AuthRole>(data!.role);

    useEffect(() => {
        if (data?.role) {
            setValue(data.role);
        }
    }, [data?.role]);

    const handleDropdownChange = (eventKey: string | null, _e: any) => {
        const agentToUpdate = fetchAgents?.find((item) => item.id === data?.id);
        if (agentToUpdate?.id) {
            UpdateAgent({
                ..._.omit(agentToUpdate, 'pictureUrl'),
                role: eventKey?.toUpperCase() as AgentAuthRoles,
                pictureUrl: agentToUpdate.pictureUrl,
            }).then(() => {
                setRefreshData((prev) => !prev);
                setValue(eventKey?.toUpperCase() as AuthRole);
            });
        }
    };

    return <RoleDropdown dropDownItems={ACCESS_OPTIONS} value={value} onChange={handleDropdownChange} />;
};

const StatusCellRenderer = ({ data, context }: ICellRendererParams<AgentDto>) => {
    const { fetchAgents, setRefreshData } = context as UserGridContext;
    const [value, setValue] = useState<string>(data?.status ?? '');

    useEffect(() => {
        if (data?.status) {
            setValue(data.status);
        }
    }, [data?.status]);

    const handleUpdateStatus = async (rowId: string, status: AgentStatus) => {
        const agentToUpdate = fetchAgents?.find((item) => item.id === rowId);
        if (agentToUpdate?.id) {
            try {
                await UpdateAgent({
                    ..._.omit(agentToUpdate, 'pictureUrl'),
                    status,
                    pictureUrl: agentToUpdate.pictureUrl,
                });
                setRefreshData((prev) => !prev);
                return true;
            } catch {
                return false;
            }
        }
        return false;
    };

    const handleDropdownChange = async (eventKey: string | null, _e: any) => {
        let isUpdateSuccess = false;
        switch (eventKey) {
            case 'ACTIVATE USER':
                isUpdateSuccess = await handleUpdateStatus(data?.id ?? '', AgentStatus.ACTIVE);
                if (isUpdateSuccess) {
                    setValue('ACTIVE');
                }
                break;
            case 'SUSPEND USER':
                isUpdateSuccess = await handleUpdateStatus(data?.id ?? '', AgentStatus.SUSPENDED);
                if (isUpdateSuccess) {
                    setValue('SUSPENDED');
                }
                break;
            default:
                break;
        }
    };

    const dropdownItems = USER_RBAC_OPTIONS.filter(
        (option) => option.event !== value && option.event !== 'REMOVE',
    );

    return data?.status !== AgentStatus.INVITED ? (
        <Dropdown onSelect={handleDropdownChange}>
            <Dropdown.Toggle className="border-0 text-text-low-emphasis d-flex gap-2 justify-content-center align-items-center">
                <Chip value={value} variant="status" />
                <RiArrowDownSLine />
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-0 gap-2 bg-body-color border-text-medium-emphasis custom-scrollbar dropdown__custom-menu">
                {dropdownItems.map((item) => (
                    <Dropdown.Item
                        key={item.id}
                        eventKey={item.value}
                        className="dropdown__menu-item d-flex align-items-center gap-2"
                    >
                        <Typography variant="body5" style={{ textTransform: 'uppercase', marginBottom: '0rem' }}>
                            {item.value}
                        </Typography>
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    ) : (
        <div className="d-flex ps-2">
            <Chip value={data?.status} variant="status" />
        </div>
    );
};

const DeleteCellRenderer = ({ data, context }: ICellRendererParams<AgentDto>) => {
    const { handleDeleteUser } = context as UserGridContext;
    return (
        <RiDeleteBin6Line
            size={20}
            className="text-text-low-emphasis"
            cursor="pointer"
            onClick={() => handleDeleteUser(data?.id ?? '')}
        />
    );
};

// ─── Column definitions (stable — defined once outside the component) ─────────

// Shared cell style — vertically centers content within the fixed-height row
const CELL_STYLE: CellStyle = { display: 'flex', alignItems: 'center', height: '100%' };
const CELL_CENTER_STYLE: CellStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' };

const USER_COLUMN_DEFS: ColDef<AgentDto>[] = [
    {
        colId: 'firstName',
        headerName: NAME,
        sortable: true,
        minWidth: 220,
        flex: 2,
        cellRenderer: NameCellRenderer,
        cellStyle: CELL_STYLE,
    },
    {
        colId: 'createdAt',
        field: 'createdAt',
        headerName: ADDED_DATE,
        sortable: true,
        minWidth: 190,
        flex: 1,
        cellRenderer: TruncatedTextCellRenderer,
        cellStyle: CELL_STYLE,
    },
    {
        colId: 'emailId',
        field: 'email',
        headerName: EMAIL_ID,
        sortable: false,
        minWidth: 220,
        flex: 3,
        cellRenderer: EmailCellRenderer,
        cellStyle: CELL_STYLE,
    },
    {
        colId: 'lastLoggedIn',
        field: 'lastLoggedIn',
        headerName: LAST_LOGGED_IN,
        sortable: false,
        minWidth: 190,
        flex: 1,
        valueGetter: (p) => p.data?.lastLoggedIn?.toString() ?? '',
        cellRenderer: TruncatedTextCellRenderer,
        cellStyle: CELL_STYLE,
    },
    {
        colId: 'access',
        headerName: ACCESS,
        sortable: false,
        width: 120,
        cellRenderer: AccessCellRenderer,
        cellStyle: CELL_STYLE,
    },
    {
        colId: 'status',
        headerName: STATUS,
        sortable: false,
        width: 130,
        cellRenderer: StatusCellRenderer,
        cellStyle: CELL_STYLE,
    },
    {
        colId: 'delete',
        headerName: '',
        sortable: false,
        width: 52,
        resizable: false,
        cellRenderer: DeleteCellRenderer,
        cellStyle: CELL_CENTER_STYLE,
    },
];

// ─── Component ────────────────────────────────────────────────────────────────

const UserManagementGrid = () => {
    const [inputValue, setInputValue] = useState<string>('');
    const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
    const [dropdownValue, setDropdownValue] = useState<string>(ACCESS_OPTIONS[0].value);
    const [_refreshData, setRefreshData] = useState<boolean>(false);
    const [disableInvite, setDisableInvite] = useState<boolean>(false);
    const [idToDelete, setIdToDelete] = useState<string>('');
    const [searchText, setSearchText] = useState<string>('');
    const [showConfirmationUserManagementModal, setShowConfirmationUserManagementModal] = useState<boolean>(false);
    const gridApiRef = useRef<GridApi<AgentDto> | null>(null);

    const handleDeleteUser = (id: string) => {
        setShowConfirmationUserManagementModal(true);
        setIdToDelete(id);
    };

    const { data: fetchInvitedAgents, refresh: refreshInvitedAgents } = useApiObjectData<AgentDto[], AgentDto[]>(
        KIND.AGENTS,
        getAgentsList,
        (a) => a,
        { status: AgentStatus.INVITED },
    );
    const { data: fetchAgents, refresh } = useApiObjectData<AgentDto[], AgentDto[]>(
        KIND.AGENTS,
        getAgentsList,
        (a) => a,
    );

    const safeAgents = Array.isArray(fetchAgents) ? fetchAgents : [];

    const { handleSearchChange: handleGridSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
        useClientExternalTextSearch<AgentDto>({
            fields: [
                (row) => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(),
                (row) => row.email,
            ],
            onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
        });

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchText(val);
        handleGridSearchChange(val);
    }, [handleGridSearchChange]);

    const handleGridReady = useCallback((event: GridReadyEvent<AgentDto>) => {
        gridApiRef.current = event.api;
    }, []);

    const handleDropdownChange = (eventKey: string | null, _e: any) => {
        setDropdownValue(eventKey ?? '');
    };

    const handleInputChange = (event: any) => {
        setInputValue(event.target.value);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setIsValidEmail(emailRegex.test(event.target.value));
    };

    const handleInviteUser = () => {
        isValidEmail &&
            inviteAgent({
                email: inputValue,
                role: dropdownValue.toUpperCase() as AgentAuthRoles,
            }).then(() => {
                setDisableInvite(true);
                setRefreshData((prev) => !prev);
                refreshInvitedAgents();
                refresh();
                setInputValue('');
                setDisableInvite(false);
            });
    };

    const mapFetchedAgentItem = (item: AgentDto): MenuItem => ({
        id: item.id,
        displayValue: item?.email,
        value: item?.email,
    });

    const invitedList = useMemo(() => {
        if (Array.isArray(fetchInvitedAgents)) {
            return fetchInvitedAgents.map((item: AgentDto) => mapFetchedAgentItem(item));
        }
        return [];
    }, [fetchInvitedAgents]);

    const handleUserDeleteClick = () => {
        deleteAgent(idToDelete).then(() => {
            setRefreshData((prev) => !prev);
            refreshInvitedAgents();
            refresh();
        });
        setShowConfirmationUserManagementModal(false);
    };

    const handleCloseUserManagementModal = () => {
        setShowConfirmationUserManagementModal(false);
    };

    const gridContext: UserGridContext = useMemo(
        () => ({ fetchAgents: safeAgents, setRefreshData, handleDeleteUser }),
        [safeAgents], // handleDeleteUser and setRefreshData are stable references
    );

    return (
        <>
            {/* ── Toolbar: search left + invite right ─────────────────────── */}
            <div className="settings-v2__user-toolbar">
                <SearchBar
                    value={searchText}
                    onChange={handleSearchChange}
                    placeholder="Search by email…"
                    searchClassName="search-container__input-cont"
                />
                <InviteSubHeaderContent
                    onInputChange={handleInputChange}
                    emailInput={inputValue}
                    isValidEmail={isValidEmail}
                    dropdownValue={dropdownValue}
                    onDropdownChange={handleDropdownChange}
                    addUser={handleInviteUser}
                    invitedUserList={invitedList}
                    disableInviteButton={disableInvite}
                />
            </div>

            {/* ── Grid ────────────────────────────────────────────────────── */}
            <AgGridBase.Root fullWidth>
                <AgGridBase.Table<AgentDto>
                    columnDefs={USER_COLUMN_DEFS}
                    dataMode="client"
                    rowData={safeAgents}
                    bodyHeight="18rem"
                    wrapperClassName="ag-theme-alpine ag-findings-grid__wrapper ag-findings-grid__wrapper--theme-findings ag-grid-theme--amp-dark user-mgmt-grid"
                    movableColumns={false}
                    defaultColDef={{
                        resizable: true,
                        suppressHeaderMenuButton: true,
                        suppressHeaderContextMenu: true,
                    }}
                    agGridProps={{
                        context: gridContext,
                        rowHeight: 52,
                        getRowId: (params) => String(params.data?.id ?? ''),
                        suppressContextMenu: true,
                        suppressCellFocus: true,
                        isExternalFilterPresent,
                        doesExternalFilterPass,
                        onGridReady: handleGridReady,
                        onGridPreDestroyed: () => { gridApiRef.current = null; },
                    }}
                />
            </AgGridBase.Root>

            <ConfirmationModal
                modalTitle="REMOVE USER"
                modalBodyTitle="Are you sure you want to remove this user?"
                onPrimaryButtonClick={handleUserDeleteClick}
                primaryButtonLabel={YES_DELETE_IT}
                show={showConfirmationUserManagementModal}
                onClose={handleCloseUserManagementModal}
                buttonVariant="danger"
            />
        </>
    );
};

export default UserManagementGrid;
